/* Loaded once in theme.liquid so it also handles dynamically opened Quick views. */
if (!customElements.get('pc-quick-view-related')) {
  customElements.define('pc-quick-view-related', class extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.loadProducts());
    }
    async loadProducts() {
      this.abort = new AbortController();
      const signal = this.abort.signal;
      try {
        const response = await fetch(this.dataset.url, { signal });
        if (!response.ok) throw new Error('Recommendations unavailable');
        const data = await response.json();
        if (signal.aborted || !this.isConnected) return;
        const seen = new Set([String(this.dataset.productId)]);
        const products = (data.products || []).filter(product => {
          const id = String(product.id);
          if (seen.has(id)) return false;
          seen.add(id); return true;
        }).slice(0, 6);
        if (!products.length) return;
        const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
          style: 'currency', currency: this.dataset.currency || 'AUD'
        });
        const heading = document.createElement('h3');
        heading.className = 'pc-qv-related__heading';
        heading.textContent = 'Similar products';
        const list = document.createElement('ul');
        list.className = 'pc-qv-related__list';
        products.forEach(product => {
          const url = new URL(product.url, location.origin);
          if (url.origin !== location.origin) return;
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.href = url.href;
          const picture = document.createElement('span');
          picture.className = 'pc-qv-related__picture';
          const imageSource = typeof product.featured_image === 'string'
            ? product.featured_image : product.featured_image?.url;
          if (imageSource) {
            const imageURL = new URL(imageSource, location.origin);
            if (['https:', 'http:'].includes(imageURL.protocol)) {
              imageURL.searchParams.set('width', '240');
              const image = document.createElement('img');
              image.src = imageURL.href;
              image.alt = '';
              image.loading = 'lazy';
              image.width = 120; image.height = 120;
              picture.append(image);
            }
          }
          const title = document.createElement('span');
          title.className = 'pc-qv-related__title';
          title.textContent = product.title;
          const price = document.createElement('span');
          price.className = 'pc-qv-related__price';
          price.textContent = (product.price_varies ? 'From ' : '') + formatter.format(product.price / 100);
          link.append(picture, title, price);
          item.append(link); list.append(item);
        });
        if (!list.children.length) return;
        this.replaceChildren(heading, list);
        this.hidden = false;
      } catch (error) {
        if (!signal.aborted) this.hidden = true;
      }
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.abort?.abort();
    }
  });
}

/* This element exists only in the Quick view branch of main-product. */
if (!customElements.get('pc-quick-view-controls')) {
  customElements.define('pc-quick-view-controls', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.bound = new WeakSet();
      this.resizers = [];
      this.rangeObservers = [];
      const scan = () => this.querySelectorAll('[data-quantity-slider]').forEach(el => this.bindQuantity(el));
      this.observer = new MutationObserver(scan);
      this.observer.observe(this, { childList: true, subtree: true });
      this.frame = requestAnimationFrame(scan);
    }
    bindQuantity(container) {
      if (this.bound.has(container)) return;
      const range = container.querySelector('[data-quantity-range]');
      const quantity = container.querySelector('[data-quantity-input]');
      if (!range || !quantity) return;
      this.bound.add(container);
      const bubble = container.querySelector('[data-quantity-bubble]');
      const minus = container.querySelector('[data-quantity-decrease]');
      const plus = container.querySelector('[data-quantity-increase]');
      const options = { signal: this.abort.signal };
      const bounds = () => {
        const min = Number(range.getAttribute('min') || 1);
        const max = Math.max(min, Number(range.getAttribute('max') || 100));
        const step = Math.max(1, Number(range.getAttribute('step')) || 1);
        return { min, max, step };
      };
      const sync = (value, notify = false) => {
        const { min, max, step } = bounds();
        let number = Number(value);
        if (!Number.isFinite(number)) number = min;
        const last = min + Math.floor((max - min) / step) * step;
        number = Math.min(last, Math.max(min, min + Math.round((number - min) / step) * step));
        quantity.value = String(number);
        range.value = String(number);
        const progress = max > min ? (number - min) / (max - min) * 100 : 0;
        range.style.setProperty('--slider-progress', progress + '%');
        if (bubble) {
          if (bubble.textContent !== String(number)) bubble.textContent = String(number);
          bubble.style.left = (12 + progress / 100 * Math.max(0, range.offsetWidth - 24)) + 'px';
        }
        if (minus) minus.disabled = range.disabled || number <= min;
        if (plus) plus.disabled = range.disabled || number >= last;
        if (notify) {
          quantity.dispatchEvent(new Event('input', { bubbles: true }));
          quantity.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };
      range.addEventListener('input', () => sync(range.value, true), options);
      quantity.addEventListener('change', () => sync(quantity.value), options);
      [[minus, -1], [plus, 1]].forEach(([button, direction]) => {
        if (!button) return;
        button.type = 'button';
        button.addEventListener('click', event => {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (!range.disabled) sync(Number(quantity.value) + direction * bounds().step, true);
        }, { ...options, capture: true });
      });
      const resize = new ResizeObserver(() => sync(quantity.value));
      resize.observe(range);
      this.resizers.push(resize);
      const limits = new MutationObserver(() => sync(quantity.value));
      limits.observe(range, { attributes: true, attributeFilter: ['min', 'max', 'step', 'disabled'] });
      this.rangeObservers.push(limits);
      sync(quantity.value || range.value);
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.abort?.abort();
      this.observer?.disconnect();
      this.resizers?.forEach(observer => observer.disconnect());
      this.rangeObservers?.forEach(observer => observer.disconnect());
    }
  });
}
